import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, X, Settings, Users, Building2 } from "lucide-react";
import { Button, Card, Badge, Spinner, Modal, Input, Textarea, FileUploadInput } from "../shared/ui";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompaniesPage = () => {
  const { companies, fetchCompanies, currentCompany, switchCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    logo_url: "",
    phone: "",
    email: "",
    address: "",
    abn: ""
  });

  useEffect(() => {
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setAllUsers(res.data);
    } catch (e) {}
  };

  const fetchCompanyUsers = async (companyId) => {
    try {
      const res = await axios.get(`${API}/companies/${companyId}/users`);
      setCompanyUsers(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenModal = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        name: company.name || "",
        code: company.code || "",
        logo_url: company.logo_url || "",
        phone: company.phone || "",
        email: company.email || "",
        address: company.address || "",
        abn: company.abn || ""
      });
    } else {
      setEditingCompany(null);
      setFormData({ name: "", code: "", logo_url: "", phone: "", email: "", address: "", abn: "" });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.id}`, formData);
        alert("Company updated!");
      } else {
        await axios.post(`${API}/companies`, formData);
        alert("Company created!");
      }
      setShowModal(false);
      fetchCompanies();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save company");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (company) => {
    if (!window.confirm(`Deactivate "${company.name}"? Users will lose access to this company.`)) return;
    try {
      await axios.delete(`${API}/companies/${company.id}`);
      alert("Company deactivated");
      fetchCompanies();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete company");
    }
  };

  const handleManageUsers = (company) => {
    setSelectedCompany(company);
    fetchCompanyUsers(company.id);
    setShowUsersModal(true);
  };

  const handleAssignUser = async (userId, role) => {
    try {
      await axios.post(`${API}/companies/${selectedCompany.id}/users`, {
        user_id: userId,
        company_id: selectedCompany.id,
        role: role
      });
      fetchCompanyUsers(selectedCompany.id);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to assign user");
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Remove this user from the company?")) return;
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}/users/${userId}`);
      fetchCompanyUsers(selectedCompany.id);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove user");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="companies-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight font-heading">Companies</h1>
          <p className="text-slate-500 mt-1">Manage your companies and team access</p>
        </div>
        <Button onClick={() => handleOpenModal()} data-testid="add-company-btn">
          <Plus className="w-5 h-5 mr-2" />
          Add Company
        </Button>
      </div>

      {/* Companies Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies.map(company => (
          <Card key={company.id} className={currentCompany?.id === company.id ? "ring-2 ring-orange-500" : ""}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {company.logo_url ? (
                  <img src={company.logo_url} alt={company.name} className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-orange-600" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold">{company.name}</h3>
                  <Badge variant="default">{company.code}</Badge>
                </div>
              </div>
              {currentCompany?.id === company.id && (
                <Badge variant="success">Active</Badge>
              )}
            </div>

            {company.email && (
              <p className="text-sm text-slate-600 mb-1">{company.email}</p>
            )}
            {company.phone && (
              <p className="text-sm text-slate-600 mb-3">{company.phone}</p>
            )}

            <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={() => handleManageUsers(company)}>
                <Users className="w-4 h-4 mr-1" />
                Users
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleOpenModal(company)}>
                <Settings className="w-4 h-4 mr-1" />
                Edit
              </Button>
              {currentCompany?.id !== company.id && (
                <Button size="sm" variant="ghost" onClick={() => handleDelete(company)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </Card>
        ))}

        {companies.length === 0 && (
          <div className="col-span-3 text-center py-12">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-bold text-slate-600">No Companies Yet</h3>
            <p className="text-slate-500 mb-4">Create your first company to get started</p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="w-5 h-5 mr-2" />
              Add Company
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Company Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingCompany ? "Edit Company" : "Add Company"}>
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Company Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Mirri Cranes and Rigging Services"
              required
            />
            <Input
              label="Company Code"
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
              placeholder="MIRRI"
              maxLength={10}
              required
            />
          </div>

          <FileUploadInput
            label="Company Logo (optional)"
            accept=".jpg,.jpeg,.png,.gif,.svg"
            onFileSelect={(fileUrl, fileName) => setFormData({...formData, logo_url: fileUrl})}
            currentFile={formData.logo_url ? "Logo uploaded" : null}
            onClear={() => setFormData({...formData, logo_url: ""})}
          />

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+61 400 000 000"
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="admin@company.com"
            />
          </div>

          <Input
            label="ABN (optional)"
            value={formData.abn}
            onChange={(e) => setFormData({...formData, abn: e.target.value})}
            placeholder="12 345 678 901"
          />

          <Textarea
            label="Address"
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            placeholder="123 Main Street, Sydney NSW 2000"
          />

          <div className="flex gap-4">
            <Button type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : (editingCompany ? "Update Company" : "Create Company")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowModal(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      {/* Manage Users Modal */}
      <Modal isOpen={showUsersModal} onClose={() => setShowUsersModal(false)} title={`Users - ${selectedCompany?.name}`} size="lg">
        <div className="space-y-6">
          {/* Current Users */}
          <div>
            <h4 className="font-bold mb-3">Assigned Users</h4>
            {companyUsers.length > 0 ? (
              <div className="space-y-2">
                {companyUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-medium">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-slate-500">{user.email} • {user.company_role}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleRemoveUser(user.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No users assigned yet</p>
            )}
          </div>

          {/* Add User */}
          <div>
            <h4 className="font-bold mb-3">Add User to Company</h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {allUsers.filter(u => !companyUsers.find(cu => cu.id === u.id)).map(user => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-300 rounded-full flex items-center justify-center text-slate-700 text-sm font-bold">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                    <div>
                      <p className="font-medium">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleAssignUser(user.id, user.role)}>
                      Add as {user.role}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CompaniesPage;
